export const UI_TO_API_SCOPE = Object.freeze({
  franchise: "franchise",
  branch: "venue",
  warehouse: "warehouse",
});
export const API_TO_UI_SCOPE = Object.freeze({
  franchise: "franchise",
  venue: "branch",
  warehouse: "warehouse",
});
export const ROLE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const enabledPermissionsCount = (permissions) =>
  Object.values(permissions || {}).filter((value) => value === true).length;

// Supply keys from the confirmed API schema for the selected scope, not the
// legacy mock permission matrix. Never coerce strings into booleans.
export function normalizeRolePermissions(permissions, supportedKeys) {
  if (!Array.isArray(supportedKeys)) {
    throw new Error("Supported role permissions are not configured");
  }
  return Object.fromEntries(
    supportedKeys
      .filter((key) => typeof permissions?.[key] === "boolean")
      .map((key) => [key, permissions[key]])
  );
}

export function buildRolePayload(values, supportedKeys, franchiseId) {
  const scope = UI_TO_API_SCOPE[values.portalType];
  if (!scope) throw new Error("Select a valid Portal Type");
  if (!ROLE_STATUS_OPTIONS.some(({ value }) => value === values.status)) {
    throw new Error("Select a valid Status");
  }
  return {
    name: values.name.trim(),
    scope,
    permissions: normalizeRolePermissions(values.permissions, supportedKeys),
    status: values.status,
    ...(franchiseId ? { franchiseId } : {}),
  };
}

const ERROR_MESSAGES = {
  400: "Check the role details and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You do not have permission to access this role.",
  404: "Role not found.",
  409: "A role with this name already exists.",
  500: "Unable to save or load roles. Please try again.",
};

export function roleErrorMessage(error) {
  // The shared Axios interceptor rejects response.data directly.
  const body = error?.response?.data || error;
  const status = error?.response?.status || body?.code || body?.status;
  const message = typeof body?.message === "string" ? body.message : body;
  if (
    typeof message === "string" &&
    message.trim() &&
    message.length < 500 &&
    !/[<>]|\n\s*at\s/.test(message) &&
    message !== "[object Object]" &&
    message !== "Something went wrong"
  )
    return message;
  return (
    ERROR_MESSAGES[status] ||
    "Unable to reach the Roles service. Please try again."
  );
}

export const currentFranchiseId = (user) =>
  user?.franchiseId?.id ||
  user?.franchiseId?._id ||
  (typeof user?.franchiseId === "string" ? user.franchiseId : "");

export function fromApiRole(role) {
  if (!role || !(role.id || role._id) || !API_TO_UI_SCOPE[role.scope]) {
    throw new Error("Invalid role response from server");
  }
  return {
    ...role,
    id: role.id || role._id,
    portalType: API_TO_UI_SCOPE[role.scope],
    permissions: role.permissions || {},
  };
}

// Confirmed in role.controller -> Role.paginate, not inferred from other APIs.
export function readRolesPage(response) {
  const body = response.data;
  if (
    !Array.isArray(body?.results) ||
    !Number.isFinite(body.totalResults) ||
    !Number.isFinite(body.totalPages)
  ) {
    throw new Error("Invalid Roles pagination response from server");
  }
  return { ...body, results: body.results.map(fromApiRole) };
}

export const permissionLabel = (key) =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (letter) => letter.toUpperCase());
